"use client"

import * as React from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface MultiSelectOption<T extends string | number = number> {
  id: T
  text: string
}

interface MultiSelectProps<T extends string | number = number> {
  options: MultiSelectOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function MultiSelect<T extends string | number = number>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className,
}: MultiSelectProps<T>) {
  const [open, setOpen] = React.useState(false)

  const toggle = (id: T) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id])
  }

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  const selectedLabels = options.filter(o => value.includes(o.id))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className="flex-1 text-left truncate">
            {selectedLabels.length === 0 ? (
              <span className="text-foreground">{placeholder}</span>
            ) : selectedLabels.length === 1 ? (
              <span>{selectedLabels[0].text}</span>
            ) : (
              <span>{selectedLabels.length} selected</span>
            )}
          </span>
          <span className="flex items-center gap-1 ml-2 shrink-0">
            {selectedLabels.length > 0 && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={clear}
              />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-1"
        align="start"
        sideOffset={4}
      >
        <div role="listbox" aria-multiselectable="true" className="max-h-96 overflow-y-auto">
          {options.map(option => {
            const isSelected = value.includes(option.id)
            return (
              <button
                key={String(option.id)}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
                  "hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                  isSelected && "bg-accent/50"
                )}
                onClick={() => toggle(option.id)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault()
                    toggle(option.id)
                  }
                }}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {isSelected && <Check className="h-4 w-4" />}
                </span>
                {option.text}
              </button>
            )
          })}
          {options.length === 0 && (
            <p className="text-sm text-muted-foreground px-2 py-4 text-center">No options available</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
